<script setup lang="ts">
import { computed, ref } from 'vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import type { ActionPermissions } from '@/components/limit_action_copy'
import ShareLinkPanel from './ShareLinkPanel.vue'
import { shareSavedGoal, unshareSavedGoal } from './api'
import { LIBRARY_EVENT_NAMES } from './library_event_names'
import { useShareLink } from './useShareLink'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'

const props = withDefaults(defineProps<{
  uuid: string
  shareState: boolean
  shareLinkState?: string
  sharePermissions: ActionPermissions
}>(), {
  shareLinkState: ''
})

const isBlockedModalOpen = ref(false)
const { sharing, shared, shareLink, shareContent, unshareContent } = useShareLink(
  () => ({ uuid: props.uuid, shareState: props.shareState, shareLinkState: props.shareLinkState }),
  { share: shareSavedGoal, unshare: unshareSavedGoal, contentLabel: 'goal' }
)

const analytics = getAnalyticsTracker().getChildContext({
  type: CONTENT_TYPES.SAVED_SUPER_PAGE,
  uuid: computed(() => props.uuid),
  share_link: computed(() => shareLink.value)
})

async function shareGoal() {
  if (props.sharePermissions.block) {
    isBlockedModalOpen.value = true
    return
  }
  analytics.sendEvent(LIBRARY_EVENT_NAMES.SHARE_GOAL.CLICKED_CREATE_SHARE_LINK)
  if (await shareContent()) {
    analytics.sendEvent(LIBRARY_EVENT_NAMES.SHARE_GOAL.SHARED)
    analytics.sendEvent(LIBRARY_EVENT_NAMES.SHARE_GOAL.CREATED_SHARE_LINK, { uuid: props.uuid })
  }
}

function recordLinkCopied() {
  analytics.sendEvent(LIBRARY_EVENT_NAMES.COPIED_SHARE_LINK, {
    share_content_type: CONTENT_TYPES.SAVED_SUPER_PAGE
  })
}
</script>

<template>
  <ShareLinkPanel
    :is-shared="shared"
    :share-link="shareLink"
    :is-sharing="sharing"
    link-label="Link to share goal"
    @share="shareGoal"
    @unshare="unshareContent"
    @copied="recordLinkCopied"
  />
  <PremiumActionModal v-model:external-open="isBlockedModalOpen" :action-permissions="sharePermissions" />
</template>
